import { AimOutlined, EnvironmentOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Input, message, Row, Select, Space, Typography } from "antd";
import axios from 'axios';
import L from 'leaflet';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useState } from 'react';
import { FeatureGroup, MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import './style.css';

const { Option } = Select;
const { Title } = Typography;

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png",
});

const WorkPlaceArea = () => {
    const [userLocation, setUserLocation] = useState(null);
    const [map, setMap] = useState(null);
    const [createdShapeCoordinates, setCreatedShapeCoordinates] = useState(null);
    const [customCoordinates, setCustomCoordinates] = useState({ lat: '', lon: '' });
    const [officeNames, setOfficeNames] = useState([]);
    const [officeDetails, setOfficeDetails] = useState(null);
    const [selectedOffice, setSelectedOffice] = useState('');

    const handleOfficeChange = async (value) => {
        if (value === "addNew") {
            setSelectedOffice(value);
            setCustomCoordinates({ lat: '', lon: '' });
            setOfficeDetails(null);

            if (map) {
                map.eachLayer((layer) => {
                    if (layer instanceof L.Rectangle) {
                        map.removeLayer(layer);
                    }
                });
            }
        } else {
            setSelectedOffice(value);
            try {
                const response = await axios.get(`setting/getOfficeCoordinates/${value}`);
                const officeData = response.data;

                if (officeData) {
                    setOfficeDetails(officeData);
                    const lat = officeData.officeArea[0].cLat;
                    const lon = officeData.officeArea[0].cLong;
                    setCustomCoordinates({ lat, lon });

                    const LocationCorr = [lat, lon];
                    setUserLocation(LocationCorr);

                    if (map) {
                        map.eachLayer((layer) => {
                            if (layer instanceof L.Rectangle) {
                                map.removeLayer(layer);
                            }
                        });

                        map.flyTo(LocationCorr, map.getMaxZoom());

                        const rectangleLayer = L.rectangle([
                            [officeData.officeArea[0].lat1, officeData.officeArea[0].long1],
                            [officeData.officeArea[0].lat2, officeData.officeArea[0].long2],
                        ], { color: 'red' });

                        rectangleLayer.addTo(map);
                    }
                }
            } catch (error) {
                console.error('Error fetching office details:', error.message);
                message.error('Failed to fetch office details');
            }
        }
    };

    const handleEnteredCoordinates = () => {
        const newLocation = [parseFloat(customCoordinates.lat), parseFloat(customCoordinates.lon)];
        setUserLocation(newLocation);
        if (map) {
            map.flyTo(newLocation, map.getZoom());
        }
    };

    useEffect(() => {
        axios.get('setting/getCompanySetting')
            .then(response => {
                const officeNamesArray = response.data.map(office => ({
                    id: office.id,
                    name: office.company_name
                }));
                setOfficeNames(officeNamesArray);
            })
            .catch(error => {
                console.error('Error fetching office names:', error.message);
                message.error('Failed to fetch office list');
            });

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setUserLocation([position.coords.latitude, position.coords.longitude]);
            },
            (error) => {
                console.error('Error getting user location:', error.message);
                message.error('Failed to get current location');
            }
        );
    }, []);

    const LocateControl = () => {
        const mapInstance = useMapEvents({
            click() {
                mapInstance.locate();
            },
            locationfound(e) {
                setUserLocation([e.latlng.lat, e.latlng.lng]);
                mapInstance.flyTo(e.latlng, mapInstance.getZoom());
            },
        });
        setMap(mapInstance);
        return null;
    };

    const _created = (e) => {
        const { layerType, layer } = e;
        if (layerType === 'rectangle') {
            const bounds = layer.getBounds();
            setCreatedShapeCoordinates({
                southwest: bounds.getSouthWest(),
                northeast: bounds.getNorthEast(),
            });
        }
    };

    const handleAddOffice = async () => {
        const oId = parseInt(selectedOffice);
        const officeData = {
            company_id: oId,
            officeId: oId,
            lat1: createdShapeCoordinates ? createdShapeCoordinates.northeast.lat : userLocation[0],
            long1: createdShapeCoordinates ? createdShapeCoordinates.northeast.lng : userLocation[1],
            lat2: createdShapeCoordinates ? createdShapeCoordinates.southwest.lat : null,
            long2: createdShapeCoordinates ? createdShapeCoordinates.southwest.lng : null,
            cLat: customCoordinates ? parseFloat(customCoordinates.lat) : null,
            cLong: customCoordinates ? parseFloat(customCoordinates.lon) : null,
            status: true
        };

        try {
            await axios.post('setting/addOfficeLocation', officeData);
            message.success('Office location added successfully!');
        } catch (error) {
            console.error('Error adding office:', error.message);
            message.error('Failed to add office location');
        }
    };

    

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Title level={4}>
                        <EnvironmentOutlined /> Office Location Management
                    </Title>
                    
                    <Row gutter={[24, 24]}>
                    <Col xs={24} lg={24}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ marginBottom: 8 }}>Office Name</div>
            <Select
                style={{ width: '100%' }}
                value={selectedOffice}
                onChange={handleOfficeChange}
                placeholder="Select an office"
            >
                <Option value="addNew">Add New Office</Option>
                {officeNames.map((office) => (
                    <Option key={office.id} value={office.id}>
                        {office.name}
                    </Option>
                ))}
            </Select>

            {officeDetails && (
                <div>
                    <div style={{ marginBottom: 8 }}>Office Address</div>
                    <Input value={officeDetails.address} readOnly />
                </div>
            )}

            <Row gutter={16}>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>Latitude</div>
                    <Input
                        placeholder="Enter latitude"
                        value={customCoordinates.lat}
                        onChange={(e) => setCustomCoordinates({ ...customCoordinates, lat: e.target.value })}
                    />
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>Longitude</div>
                    <Input
                        placeholder="Enter longitude"
                        value={customCoordinates.lon}
                        onChange={(e) => setCustomCoordinates({ ...customCoordinates, lon: e.target.value })}
                    />
                </Col>
            </Row>

            <Space>
                <Button icon={<AimOutlined />} onClick={handleEnteredCoordinates}>
                    Locate
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddOffice}>
                    Add Office
                </Button>
            </Space>
        </Space>
    </Col>


    <Col xs={24}>
        <div style={{ height: 400, border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
            <MapContainer
                style={{ width: '100%', height: '100%' }}
                center={userLocation || [28.7041, 77.1025]}
                zoom={userLocation ? 15 : 8}
            >
                <FeatureGroup>
                    <EditControl
                        position="topright"
                        onCreated={_created}
                        draw={{
                            rectangle: true,
                            circle: false,
                            circlemarker: false,
                            marker: false,
                            polyline: false,
                            polygon: false,
                        }}
                    />
                </FeatureGroup>
                <TileLayer
                    attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {userLocation && (
                    <Marker position={userLocation}>
                        <Popup>
                            <div>
                                <strong>Your Location</strong>
                                <p>Lat: {userLocation[0].toFixed(6)}</p>
                                <p>Lng: {userLocation[1].toFixed(6)}</p>
                            </div>
                        </Popup>
                    </Marker>
                )}
                <LocateControl />
            </MapContainer>
        </div>
    </Col>
</Row>
                </Space>
            </Card>
        </div>
    );
};

export default WorkPlaceArea;